import { getSessionStorage } from '@/utils/sessionStorage'
import { isNonEmptyString, isObject } from '@/utils/checks'
import normalizeSearchState from './normalizeSearchState'
import { SearchState } from '@/types/models'
import { CustomSearchState } from '@/types/models'
import { getSessionStorageSearchState } from '@/utils/search/searchState'
import { DISTANCE_OPTIONS } from '../filterOptions/constants'
import { SEARCH_AROUND_ME_LOCATION_NAME } from '../browseBy/constants'
import { SortByType } from './constants'

interface Taxonomy {
  type: string
  label: string
  remove?: boolean
}

export interface GlobalFilterableTaxonomy {
  type: string
  global: boolean
  title: string
}

interface RefineSearchStateFunction {
  (
    props: {
      taxonomy: Taxonomy
    },
    fromFacetOrLocationPage: boolean
  ): Partial<SearchState>
}

const refineSearchState: RefineSearchStateFunction = (
  { taxonomy },
  fromFacetOrLocationPage = false
) => {
  const searchState: CustomSearchState = getSessionStorageSearchState() || {}
  const globalFilterableTaxonomy = (getSessionStorage(
    'globalFilterableTaxonomy'
  ) || []) as Array<GlobalFilterableTaxonomy>

  if (!isObject(taxonomy) || !taxonomy.type || !taxonomy.label) {
    return searchState
  }

  if (
    taxonomy.type === 'aroundLatLngViaIP' &&
    taxonomy.label === SEARCH_AROUND_ME_LOCATION_NAME
  ) {
    return normalizeSearchState({
      ...searchState,
      configure: {
        ...searchState.configure,
        aroundLatLngViaIP: true
      },
      refinementList: {
        ...searchState.refinementList,
        locations: []
      },
      locationName: SEARCH_AROUND_ME_LOCATION_NAME,
      customLocationRadiusInMiles: DISTANCE_OPTIONS.US[3].value.toString()
    })
  }

  const refinementList = searchState?.refinementList || {}
  const filterToRefine = refinementList[taxonomy.type] || []
  const filterToRefineNormalized = Array.isArray(filterToRefine)
    ? filterToRefine
    : [filterToRefine]

  const updatedRefinementList = [...filterToRefineNormalized]
  if (!taxonomy.remove) {
    updatedRefinementList.push(taxonomy.label)
  } else {
    const index = updatedRefinementList.indexOf(taxonomy.label)
    if (index > -1) updatedRefinementList.splice(index, 1)
  }

  const isLocationTaxonomy = taxonomy.type === 'locations'
  const isGlobal = globalFilterableTaxonomy.some(
    (globalTaxonomy) =>
      globalTaxonomy.type === taxonomy.type &&
      globalTaxonomy.title === taxonomy.label
  )
  const isFirstAndOnlyFilter =
    Object.keys(refinementList).length === 0 &&
    !isNonEmptyString(searchState.locationName)

  const stateUpdate: Partial<SearchState> = {
    ...searchState,
    refinementList: {
      ...refinementList,
      [taxonomy.type]: [...new Set(updatedRefinementList)]
    },
    configure: {
      ...searchState.configure,
      page: 0
    }
  }

  if (!isGlobal && !isLocationTaxonomy && isFirstAndOnlyFilter) {
    stateUpdate.refinementList!.locations = 'United States'
  }

  if (fromFacetOrLocationPage && isLocationTaxonomy) {
    stateUpdate.refinementList!.locations = []
  }

  if (!stateUpdate.sortBy) {
    stateUpdate.sortBy = SortByType.Relevance
  }

  return normalizeSearchState(stateUpdate)
}

export default refineSearchState
